import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { closeMobileNav, setHeaderNavDisplaystate } from '../actions/navigationActions';
import { routeLocationUpdated } from '../actions/pageActions';
import linkResolver from '../helpers/linkResolver/linkResolver';
import { getQueryParam } from '../helpers/url/urlHelpers';
import INavigationItem from '../interfaces/navigation/navigationItem';
import ISettingsViewModel from '../interfaces/umbracoNodes/settingsViewModel';
import NotFound from '../pages/notFoundV2';
import { RootState } from '../store';
import mappedRoutes from './routeConfig';
import StyledRouteTransitions from './routeTransitions.styles';
import { resetToInitalValues } from '../actions/registerShipmentActions';

type Props = {
	routes: INavigationItem[];
	history: any;
	location: any;
};

const Routes: FC<Props> = ({ routes, history, location }) => {
	const dispatch: ThunkDispatch<RootState, unknown, AnyAction> = useDispatch();
	const prevRoute = useRef(window.location.pathname);
	const [allMappedRoutes, setAllMappedRoutes] = useState(null);
	const { settings }: { settings: ISettingsViewModel } = useSelector((state: RootState) => state.pageReducer);
	
	// ------------ Returns feature for Metta Sport ------------ 
	// The reason this is in this file instead of in myRegisterShipment is that myRegisterShipment is re-rendered and-
	//  -the states are reinitalized as well which doesn't happen here.
	const isReturnParam = getQueryParam('isReturn') === 'true';
	const [isReturn, setIsReturn] = useState<boolean>(isReturnParam);
	
	useEffect(() => {
		// Detect when isReturn query parameter is changed so we know when to reset to initial values
		// We only want to reset when the user is going from the Returns page to a non-Returns page
		// because if we'd always reset then the user would lose progress if they switch between normal reg.shipm. page and another page
		if (isReturn !== isReturnParam) {
			if (isReturn && !isReturnParam) {
				dispatch(resetToInitalValues());
				setIsReturn(isReturnParam);
			}
		}
	}, [isReturnParam, dispatch, isReturn]);
	// ---------------------------------------------------------
	
	useEffect(() => {
		setAllMappedRoutes(mappedRoutes(routes));
	}, [routes]);

	useEffect(() => {
		const listen = history.listen(loc => {
			const path = routes.find(route => route.Url === loc.pathname);
			// Page reload is necessary if the page contains a form that hasn't been loaded before
			if (path && path.FormPage && prevRoute.current !== window.location.pathname) {
				prevRoute.current = window.location.pathname;
				window.location.reload();
				return;
			}
			prevRoute.current = window.location.pathname;
			dispatch(setHeaderNavDisplaystate(false, 0));
			dispatch(closeMobileNav());
			dispatch(routeLocationUpdated(linkResolver(loc.pathname)));
		});
		return () => {
			listen();
		};
	}, [dispatch, history, routes]);

	if (location.pathname === '/callback') {
		const error = getQueryParam('error');
		const returnUrl = window.sessionStorage.getItem('returnUrl');
		const onboardingTab = window.sessionStorage.getItem('onboardingTab');
		if (onboardingTab && !error) window.sessionStorage.setItem('onboardingTab', String(Number(onboardingTab) + 1));

		// @ts-ignore
		if (error == null) window.location = returnUrl;
		// @ts-ignore
		else if (returnUrl.includes('?')) window.location = returnUrl + '&error=' + error;
		// @ts-ignore
		else window.location = returnUrl + '?error=' + error;

		return <></>;
	}
	return (
		<StyledRouteTransitions>
			<Switch location={location}>
				<Route
					exact
					path="/app"
					component={() =>
						// eslint-disable-next-line no-nested-ternary
						/android/i.test(window.navigator.userAgent)
							? (window.location.href = settings.Values.PlayStore.Url)
							: /iPad|iPhone|iPod/i.test(window.navigator.userAgent)
							? (window.location.href = settings.Values.AppStore.Url)
							: (window.location.href = '/minar-sidur')
					}
				/>
				<Route exact path="/frettir/almennar-frettir" render={() => <Redirect to="/frettir" />} />
				<Route exact path="/frettir/blogg" render={() => <Redirect to="/frettir" />} />
				<Route
					exact
					path="/breyta"
					render={() => <Redirect to="/einstaklingar/mottaka/breyta-afhendingarstad/" />}
				/>
				{allMappedRoutes}
				{allMappedRoutes !== null && <Route component={NotFound} />}
			</Switch>
		</StyledRouteTransitions>
	);
};

export default withRouter(Routes);
